// PassageCeintureGrid.jsx (version tableau)
import React, { useState, useEffect, useMemo, useCallback } from "react";
import DashboardNavigation from "../../DashboardNavigation/DashboardNavigation";
import { useUser } from "../../Home/UserContext";
import { getRoleFromRoleId } from "../../../utils/roleHelper";
import useScrollToTop from "../../../hooks/useScollToTop";
import Modal from "./Modal.jsx";
import AdherentPassageCeinture from "./AdherentPassageCeinture";
import Loader from "./../../../utils/Loader.jsx";
import { ToastContainer, toast } from "react-toastify";
import { getGroupsList } from "./../../../services/groupServices";
import { getAllAdherentsListWithGroup } from "./../../../services/adherentServices";
import {
  getBeltHistoryByAdherent,
  getBeltsList,
  getBeltCountBySeason,
  addBeltHistoryBulk,
  addBeltHistory,
} from "./../../../services/beltServices";
import { getSettings } from "./../../../services/settingsServices";
import { FaInfoCircle, FaTimes } from "react-icons/fa";
import BeltCounts from "../../Home/BeltCounts";
import ScrollIndicator from "./../../Home/ScrollIndicator";
import HeaderTitle from "../../../components/HeaderTitle/HeaderTitle";


const PassageCeintureGrid = () => {
  useScrollToTop();

  // User / role
  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);

  // Data
  const [groups, setGroups] = useState([]);
  const [adherents, setAdherents] = useState([]);
  const [belts, setBelts] = useState([]);
  const [beltHistories, setBeltHistories] = useState({}); // { [adherentId]: { BeltColor, date_passage } }

  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSeason, setSelectedSeason] = useState("");
  const [seasons, setSeasons] = useState([]);
  const [filterGroup, setFilterGroup] = useState("");
  const [filterBelt, setFilterBelt] = useState("");

  const [loading, setLoading] = useState(true);

  // Selection
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkBeltId, setBulkBeltId] = useState("");
  const [bulkDate, setBulkDate] = useState("");

  // Info modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedAdherent, setSelectedAdherent] = useState(null);

  // 1) Chargement des paramètres (saison par défaut = saison_active)
  useEffect(() => {
    (async () => {
      try {
        const settings = await getSettings();
        const activeSeason = settings?.[0]?.saison_active || "";
        if (activeSeason) {
          setSelectedSeason(activeSeason); // ✅ défaut = saison active
          setSeasons([
            getPreviousSeason(activeSeason),
            activeSeason,
            getNextSeason(activeSeason),
          ]);
        } else {
          setSeasons([]);
          setSelectedSeason(""); // retombe sur "Toutes les saisons"
        }
      } catch (e) {
        console.error(e);
        toast.error("Erreur lors du chargement des paramètres.");
      }
    })();
  }, []);

  // Fetch core data when season changes
  useEffect(() => {
    if (!selectedSeason) return;
    fetchData();
  }, [selectedSeason]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [groupList, adherentsList, beltsData] = await Promise.all([
        getGroupsList(),
        getAllAdherentsListWithGroup(),
        getBeltsList(),
      ]);
      setGroups(groupList || []);
      setAdherents(adherentsList || []);
      setBelts(beltsData || []);

      // Fetch belt history per adherent for the selected season
      const beltHistoryPromises = (adherentsList || []).map((ad) =>
        getBeltHistoryByAdherent(ad.id, selectedSeason)
      );
      const histories = await Promise.all(beltHistoryPromises);
      const map = {};
      histories.forEach((h, idx) => {
        // On suppose h[0] = dernier passage de grade pour la saison sélectionnée
        map[adherentsList[idx].id] = h?.[0] || null;
      });
      setBeltHistories(map);
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors du chargement des données.");
    } finally {
      setLoading(false);
    }
  }, [selectedSeason]);

  // ⬇️ Options dérivées (remplace le bloc avec dojoOptions + groupOptions par seulement ceci)
  const groupOptions = useMemo(() => groups.map((g) => g.nom), [groups]);

  // ⬇️ Filtrage (remplace le useMemo "filtered")
  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return adherents.filter((ad) => {
      const inSeason =
        !selectedSeason || (ad.saison_active || "") === selectedSeason;

      const inSearch =
        !q ||
        (ad.nom || "").toLowerCase().includes(q) ||
        (ad.prenom || "").toLowerCase().includes(q);

      const inGroup =
        !filterGroup || ad.Groups?.some((g) => (g.nom || "") === filterGroup);

      const currentBelt = beltHistories[ad.id]?.BeltColor?.nom || "";
      const inBelt = !filterBelt || currentBelt === filterBelt;

      return inSeason && inSearch && inGroup && inBelt;
    });
  }, [
    adherents,
    searchTerm,
    filterGroup,
    filterBelt,
    selectedSeason,
    beltHistories,
  ]);

  // Helpers
  const getPreviousSeason = (currentSeason) => {
    const [startYear, endYear] = currentSeason.split("-").map(Number);
    return `${startYear - 1}-${endYear - 1}`;
  };
  const getNextSeason = (currentSeason) => {
    const [startYear, endYear] = currentSeason.split("-").map(Number);
    return `${startYear + 1}-${endYear + 1}`;
  };

  const toggleAll = (checked) => {
    if (checked) {
      setSelectedIds(new Set(filtered.map((a) => a.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const toggleOne = (id, checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const openInfo = (adherentId) => {
    setSelectedAdherent(adherentId);
    setIsModalOpen(true);
  };

  const closeInfo = () => {
    setIsModalOpen(false);
    setSelectedAdherent(null);
  };

  const handleHistoryChanged = async () => {
    await fetchData();
  };

  // ⚠️ À adapter à ton service réel si différent
  const createBeltPassage = async (adherentId, beltId, datePassage) => {
    // Exemple :
    // return beltServices.createBeltPassage({ adherent_id: adherentId, belt_id: beltId, date_passage: datePassage });
    throw new Error(
      "createBeltPassage n'est pas implémenté. Ajoute-la dans beltServices ou remappe ici."
    );
  };

  // PassageCeintureGrid.jsx — remplace la fonction applyBulk par celle-ci
  const applyBulk = async () => {
    if (selectedIds.size === 0)
      return toast.warn("Sélectionne au moins un adhérent.");
    if (!bulkBeltId) return toast.warn("Choisis une ceinture.");
    if (!bulkDate) return toast.warn("Choisis une date.");

    const adherentIds = Array.from(selectedIds).map(Number);
    const beltId = Number(bulkBeltId);
    const datePassage = bulkDate;

    try {
      setLoading(true);

      // Envoi unitaire par adhérent
      const results = await Promise.allSettled(
        adherentIds.map((id) =>
          addBeltHistory({
            adherent_id: id,
            belt_id: beltId,
            date_passage: datePassage,
          })
        )
      );

      const ok = results.filter((r) => r.status === "fulfilled").length;
      const ko = results.length - ok;

      if (ok > 0) {
        toast.success(`Passages créés: ${ok}${ko ? ` • erreurs: ${ko}` : ""}`);
        await fetchData();
      } else {
        toast.error("Aucun passage n'a pu être créé.");
      }
    } catch (e) {
      toast.error("Erreur lors de l'application du passage de grade.");
    } finally {
      setLoading(false);
      setSelectedIds(new Set());
      setBulkBeltId("");
      setBulkDate("");
    }
  };

  const allChecked =
    filtered.length > 0 && filtered.every((a) => selectedIds.has(a.id));

      const toAbsolute = (u) => {
    if (!u) return "";
    const s = String(u).trim();
    // déjà absolu, data URL ou blob
    if (/^https?:\/\//i.test(s) || s.startsWith("data:") || s.startsWith("blob:")) return s;
    // chemins d'assets front (ex: /img/...): on laisse tel quel
    if (!s.startsWith("/uploads")) return s;
    // fichiers uploadés côté API
    const base = process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "";
    return `${base}${s}`;
  };

  return (
    <section className="w-full">
          {/* Offset navbar */}
          <div className="pt-[150px]" aria-hidden />
    
          <div className="mx-auto w-full max-w-7xl px-3 sm:px-6">
            {/* Titre page */}
            <HeaderTitle
              role="Enseignant"
              title="Passage de Grades"
              align="center"
              className="mb-1"
            />
      <DashboardNavigation role={userRole} />
            <ScrollIndicator />


      {/* Carte principale */}
      <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/80 dark:bg-slate-900/60 backdrop-blur mx-3 sm:mx-6 mt-4">
        {/* Bandeau dégradé */}
        <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-6 sm:px-8 text-white">
          <h3 className="text-lg sm:text-xl font-semibold text-center">
            Gestion des passages de grade
          </h3>
          <p className="text-sm/6 opacity-95 max-w-3xl mx-auto text-center">
            Filtre par saison, groupe, ceinture & recherche par nom. Sélectionne
            plusieurs adhérents pour appliquer un passage groupé.
          </p>
        </div>
        <BeltCounts />
        {/* Toolbar filtres */}
        <div className="px-5 pt-5 pb-4 sm:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <input
              type="text"
              placeholder="Rechercher par nom ou prénom…"
              className="rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {/* 2) Sélecteur Saison dans la toolbar de filtres */}
            <select
              className="rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
              value={selectedSeason}
              onChange={(e) => setSelectedSeason(e.target.value)}
            >
              <option value="">Toutes les saisons</option>
              {seasons.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            <select
              className="rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
              value={filterGroup}
              onChange={(e) => setFilterGroup(e.target.value)}
            >
              <option value="">Tous les groupes</option>
              {groupOptions.map((g) => (
                <option key={g} value={g}>
                  {g}
                </option>
              ))}
            </select>
            <select
              className="rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
              value={filterBelt}
              onChange={(e) => setFilterBelt(e.target.value)}
            >
              <option value="">Toutes les ceintures</option>
              {belts.map((b) => (
                <option key={b.id} value={b.nom}>
                  {b.nom}
                </option>
              ))}
            </select>
          </div>

          {/* Résumé */}
          <div className="mt-4 flex items-center gap-4 text-sm text-slate-700 dark:text-slate-300">
            <span className="inline-flex items-center rounded-lg bg-slate-100 dark:bg-slate-800 px-3 py-1">
              <span className="mr-2 opacity-70">Résultats</span>
              <span className="font-semibold">{filtered.length}</span>
            </span>
            {selectedIds.size > 0 && (
              <span className="inline-flex items-center rounded-lg bg-blue-50 dark:bg-blue-900/30 px-3 py-1">
                <span className="mr-2 opacity-70">Sélection</span>
                <span className="font-semibold">{selectedIds.size}</span>
              </span>
            )}
          </div>
        </div>

        {/* Tableau */}
        <div className="px-5 pb-6 sm:px-8">
          <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800 bg-white/90 dark:bg-slate-900/40">
            <table className="min-w-full">
              <thead>
                <tr className="bg-slate-50/80 dark:bg-slate-900/80 uppercase tracking-wide text-xs">
                  {[
                    "", // checkbox
                    "Adhérent",
                    "Nom Prénom",
                    "Date de naissance",
                    "Ceinture (actuelle)",
                    "Dernier passage",
                    "Info",
                  ].map((h, idx) => (
                    <th
                      key={idx}
                      className={`px-3 py-3 text-left font-semibold text-slate-700 dark:text-white ${
                        idx === 0 ? "w-[44px]" : ""
                      }`}
                    >
                      {idx === 0 ? (
                        <input
                          type="checkbox"
                          className="h-4 w-4"
                          checked={allChecked}
                          onChange={(e) => toggleAll(e.target.checked)}
                          aria-label="Tout sélectionner"
                        />
                      ) : (
                        h
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-sm">
                {filtered.map((ad) => {
                  const hist = beltHistories[ad.id];
                  const beltName = hist?.BeltColor?.nom || "—";
                  const lastDate = hist?.date_passage
                    ? new Date(hist.date_passage).toLocaleDateString("fr-FR")
                    : "—";
                  const photo =
                    toAbsolute(ad.photo_url) || toAbsolute(ad.photo) || "/img/user_avatar.jpg";

                  return (
                    <tr
                      key={ad.id}
                      className="group transition-colors hover:bg-slate-50/70 dark:hover:bg-slate-800/40"
                    >
                      <td className="px-3 py-3">
                        <input
                          type="checkbox"
                          className="h-4 w-4"
                          checked={selectedIds.has(ad.id)}
                          onChange={(e) => toggleOne(ad.id, e.target.checked)}
                          aria-label={`Sélectionner ${ad.nom} ${ad.prenom}`}
                        />
                      </td>

                      <td className="px-3 py-3">
                        <img
                          src={photo}
                          alt={`${ad.prenom} ${ad.nom}`}
                          className="h-10 w-10 rounded-full object-cover ring-1 ring-slate-200 dark:ring-slate-700"
                          onError={(e) => {
                            e.currentTarget.src = "/img/user_avatar.jpg";
                          }}
                        />
                      </td>

                      <td className="px-3 py-3 whitespace-nowrap">
                        {ad.nom} {ad.prenom}
                      </td>

                      <td className="px-3 py-3 whitespace-nowrap">
                        {ad.date_naissance
                          ? new Date(ad.date_naissance).toLocaleDateString(
                              "fr-FR"
                            )
                          : "—"}
                      </td>

                      <td className="px-3 py-3 whitespace-nowrap">
                        {beltName}
                      </td>

                      <td className="px-3 py-3 whitespace-nowrap">
                        {lastDate}
                      </td>

                      <td className="px-3 py-3 whitespace-nowrap">
                        <button
                          onClick={() => openInfo(ad.id)}
                          className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-600 text-white hover:bg-blue-700"
                          title="Chronologie & détails"
                          aria-label="Chronologie & détails"
                        >
                          <FaInfoCircle className="text-[14px]" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && !loading && (
                  <tr>
                    <td
                      className="px-3 py-6 text-center text-slate-500 dark:text-slate-400"
                      colSpan={7}
                    >
                      Aucun résultat avec ces filtres.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {loading && (
            <div className="py-6">
              <Loader />
            </div>
          )}
        </div>
      </div>

      {/* Barre d'actions groupées */}
      {selectedIds.size > 0 && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40">
          <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-3 rounded-2xl bg-white/95 dark:bg-slate-900/95 shadow-2xl ring-1 ring-slate-200/70 dark:ring-slate-700/70 px-4 py-3">
            <span className="text-sm text-slate-700 dark:text-slate-200">
              {selectedIds.size} sélectionné{selectedIds.size > 1 ? "s" : ""}
            </span>
            <select
              className="rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2"
              value={bulkBeltId}
              onChange={(e) => setBulkBeltId(e.target.value)}
            >
              <option value="">Ceinture…</option>
              {belts.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.nom}
                </option>
              ))}
            </select>
            <input
              type="date"
              className="rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2"
              value={bulkDate}
              onChange={(e) => setBulkDate(e.target.value)}
            />
            <button
              onClick={applyBulk}
              className="inline-flex items-center gap-2 rounded-xl px-4 py-2 bg-blue-600 text-white hover:bg-blue-700"
              title="Appliquer à la sélection"
            >
              Appliquer
            </button>
            <button
              onClick={() => setSelectedIds(new Set())}
              className="inline-flex items-center gap-2 rounded-xl px-3 py-2 bg-slate-100 text-slate-900 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
              title="Vider la sélection"
            >
              <FaTimes />
              <span className="hidden sm:inline">Annuler</span>
            </button>
          </div>
        </div>
      )}
      </div>

      {/* Modale info / chronologie */}
      {isModalOpen && (
        <div className="fixed inset-0 z-[70]">
          <div
            className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
            onClick={closeInfo}
            aria-hidden
          />
          <div className="absolute inset-0 flex items-center justify-center p-4">
            <div className="w-full max-w-[860px] bg-white dark:bg-slate-900 rounded-2xl shadow-2xl ring-1 ring-slate-200/70 dark:ring-slate-700/70 flex flex-col max-h-[85vh] overflow-hidden">
              <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-4 flex items-center justify-between rounded-t-2xl">
                <h3 className="text-white font-semibold">
                  Chronologie des passages
                </h3>
                <button
                  onClick={closeInfo}
                  className="inline-flex items-center gap-2 rounded-xl bg-white/10 px-3 py-1.5 text-white hover:bg-white/20"
                >
                  <FaTimes />
                  <span>Fermer</span>
                </button>
              </div>

              <div className="p-5 overflow-y-auto">
                <AdherentPassageCeinture
                  adherentId={selectedAdherent}
                  onBeltHistoryChange={handleHistoryChanged}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      <ToastContainer />
    </section>
  );
};

export default PassageCeintureGrid;
